import axios, { AxiosError } from 'axios';

// Error types for categorization
export enum ErrorType {
  NETWORK = 'NETWORK',
  API = 'API',
  AUTH = 'AUTH',
  TIMEOUT = 'TIMEOUT',
  PARSING = 'PARSING',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN',
  DATABASE = 'DATABASE',
  CACHE = 'CACHE',
}

// Error severity levels
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

// Interface for structured error logging
export interface ErrorLog {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  originalError?: unknown;
  userMessage?: string;
}

// Track errors in development console and in production
let errorLogs: ErrorLog[] = [];

// Function to get the last N errors
export const getRecentErrors = (count: number = 10): ErrorLog[] => {
  return errorLogs.slice(-count);
};

// Function to clear the error logs
export const clearErrorLogs = (): void => {
  errorLogs = [];
};

// Main error logging function
export const logError = (
  error: unknown,
  type: ErrorType = ErrorType.UNKNOWN,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  context: Record<string, unknown> = {},
  userMessage?: string
): ErrorLog => {
  const timestamp = new Date().toISOString();
  
  // Determine error type if not specified
  if (type === ErrorType.UNKNOWN) {
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        type = ErrorType.TIMEOUT;
      } else if (error.code === 'ERR_NETWORK') {
        type = ErrorType.NETWORK;
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        type = ErrorType.AUTH;
      } else {
        type = ErrorType.API;
      }
    } else if (error instanceof SyntaxError) {
      type = ErrorType.PARSING;
    }
  }
  
  // Format error message
  let message = 'An unknown error occurred';
  
  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    const responseData = axiosError.response?.data as { detail?: string } | undefined;
    message = responseData?.detail || axiosError.message;
    
    // Add request/response details to context
    context = {
      ...context,
      url: axiosError.config?.url,
      method: axiosError.config?.method,
      status: axiosError.response?.status,
      data: axiosError.response?.data,
    };
  }
  
  // Create error log entry
  const errorLog: ErrorLog = {
    type,
    severity,
    message,
    timestamp,
    context,
    originalError: error,
    userMessage,
  };
  
  // Add to error log array
  errorLogs.push(errorLog);
  
  // In development, log to console
  if (import.meta.env.DEV) {
    console.error(`[${type}] ${message}`, {
      severity,
      context,
      originalError: error,
    });
  }
  
  // In production, we could send to a logging service
  if (import.meta.env.PROD) {
    // Example: send to a logging service
    // sendToLoggingService(errorLog);
    
    // If this is a critical error, we might want to show a notification or redirect
    if (severity === ErrorSeverity.CRITICAL) {
      // Example: show a global error notification
      // showGlobalErrorNotification(userMessage || "A critical error occurred. The team has been notified.");
    }
  }
  
  return errorLog;
};

// Helper for database-related errors
export const logDatabaseError = (
  error: unknown,
  context: Record<string, unknown> = {},
  userMessage: string = "There was an issue accessing the database."
): ErrorLog => {
  return logError(
    error,
    ErrorType.DATABASE,
    ErrorSeverity.ERROR,
    context,
    userMessage
  );
};

// Helper for cache-related errors
export const logCacheError = (
  error: unknown,
  context: Record<string, unknown> = {},
  userMessage: string = "There was an issue with the cached data."
): ErrorLog => {
  return logError(
    error,
    ErrorType.CACHE,
    ErrorSeverity.WARNING,
    context,
    userMessage
  );
};

// Function to get user-friendly error message
export const getUserErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    
    if (!axiosError.response) {
      return "Network error. Please check your internet connection.";
    }
    
    switch (axiosError.response.status) {
      case 400:
        return "The request was invalid. Please check your inputs.";
      case 401:
        return "You need to be logged in to perform this action.";
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 409:
        return "There was a conflict with the current state of the resource.";
      case 500:
        return "Something went wrong on the server. Please try again later.";
      default:
        return `Server error (${axiosError.response.status}). Please try again later.`;
    }
  }
  
  return "An unexpected error occurred. Please try again.";
};

// Function to check if the error is specifically about the database vs. cache
export const isDatabaseError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    const responseData = (error as AxiosError)?.response?.data as Record<string, unknown> | undefined;
    const detail = typeof responseData?.detail === 'string' ? responseData.detail : '';
    return (
      detail.includes('database') ||
      detail.includes('DB') ||
      detail.includes('SQL')
    );
  }
  return false;
};

export const isCacheError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    const responseData = (error as AxiosError)?.response?.data as Record<string, unknown> | undefined;
    const detail = typeof responseData?.detail === 'string' ? responseData.detail : '';
    return (
      detail.includes('cache') ||
      detail.includes('JSON') ||
      responseData?.source === 'cache'
    );
  }
  return false;
}; 